// Wi-Fi management for a device, over MQTT (matches the mobile app):
//   list   → publish `getallwifi`; device replies on <uno>/client with
//            ';'-separated `ssid,pass` entries after the ']'.
//   add    → `ADDWIFIssid=<ssid>pass=<password>`
//   remove → `REMOVEWIFIssid=<ssid>pass=<password>`

import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, List, ListItem, ListItemText,
  CircularProgress, Alert, Divider, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import { useApp } from '../state/AppContext';
import { publishCommand } from '../utils/deviceController';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';

export default function DeviceWifiPanel({ device }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [list, setList] = useState(null); // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const [msg, setMsg] = useState(null);
  const awaitingRef = useRef(false);
  const timerRef = useRef(null);

  const fetchWifi = () => {
    setLoading(true);
    setMsg(null);
    awaitingRef.current = true;
    publishCommand(uno, 'getallwifi', userId);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (awaitingRef.current) {
        awaitingRef.current = false;
        setLoading(false);
        setList([]);
        setMsg({ type: 'warning', text: 'No response — the device may be offline.' });
      }
    }, 6000);
  };

  useEffect(() => {
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic || !awaitingRef.current) return;
      const start = payload.indexOf(']');
      let content = start >= 0 ? payload.slice(start + 1) : payload;
      const hash = content.indexOf('#');
      if (hash >= 0) content = content.slice(0, hash);
      // Ignore ordinary numeric status (e.g. "0,1,0"); a wifi list has letters or ';'.
      if (!/[a-zA-Z]/.test(content) && !content.includes(';')) return;
      awaitingRef.current = false;
      clearTimeout(timerRef.current);
      const nets = content.split(';').map((s) => s.trim()).filter(Boolean).map((entry) => {
        const parts = entry.split(',');
        return { ssid: (parts[0] || '').trim(), pass: (parts[1] || '').trim() };
      }).filter((n) => n.ssid);
      setList(nets);
      setLoading(false);
    });
    fetchWifi();
    return () => { off(); unsubscribeTopic(topic); clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno]);

  const addWifi = () => {
    if (!ssid.trim()) return;
    publishCommand(uno, `ADDWIFIssid=${ssid.trim()}pass=${pass}`, userId);
    setMsg({ type: 'success', text: `Adding "${ssid.trim()}"…` });
    setSsid('');
    setPass('');
    setTimeout(fetchWifi, 1800);
  };

  const removeWifi = (n) => {
    publishCommand(uno, `REMOVEWIFIssid=${n.ssid}pass=${n.pass}`, userId);
    setMsg({ type: 'success', text: `Removing "${n.ssid}"…` });
    setTimeout(fetchWifi, 1800);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>Saved Wi‑Fi networks</Typography>
        <Tooltip title="Refresh">
          <span><IconButton size="small" onClick={fetchWifi} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      </Box>

      {msg && <Alert severity={msg.type} sx={{ mb: 1 }}>{msg.text}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
      ) : list && list.length ? (
        <List dense disablePadding>
          {list.map((n) => (
            <ListItem
              key={n.ssid} disableGutters
              secondaryAction={(
                <IconButton edge="end" size="small" onClick={() => removeWifi(n)}><DeleteIcon fontSize="small" /></IconButton>
              )}
            >
              <WifiIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <ListItemText primary={n.ssid} />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No saved networks.</Typography>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Add a network</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="SSID" value={ssid} onChange={(e) => setSsid(e.target.value)} sx={{ flex: 1, minWidth: 130 }} />
        <TextField size="small" label="Password" value={pass} onChange={(e) => setPass(e.target.value)} sx={{ flex: 1, minWidth: 130 }} />
        <Button variant="contained" onClick={addWifi} disabled={!ssid.trim()}>Add</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Sent to the device over MQTT; the list refreshes after a moment.
      </Typography>
    </Box>
  );
}
